import { makeStyles } from '@lifeomic/chroma-react/styles';
import { alpha } from '@mui/material/styles';

export const OFFSET_CONTENT_HEIGHT = 168;

export const useStyles = makeStyles(theme => ({
  pageRoot: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.black[50],
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    height: `calc(100vh - ${theme.pxToRem(72)})`,
    maxHeight: `calc(100vh - ${theme.pxToRem(72)})`,
    overflow: 'hidden',
    padding: `${theme.spacing(2)} ${theme.spacing(3)} 0`,
    width: '100%',
  },
  tabsControls: {
    backgroundColor: theme.palette.background.paper,
    // The full-width divider beneath the tab row. The default (underline) Tab
    // variant draws its 4px selected indicator at the bottom of the tab, which
    // lines up with this border once the row has no bottom padding.
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(2),
    padding: theme.spacing(1, 3, 0),
    // Let the bar own the full-width divider; the TabList sizes to its tabs.
    // The underline indicator then meets the bar's bottom border instead of the
    // TabList's own one.
    '& [role="tablist"]': {
      border: 'none',
      width: 'auto',
    },
  },
  /**
   * Shared filter control width (status/system Select) used in every tab's
   * list filter bar so the search field gets the remaining width.
   */
  panelFilterControl: {
    minWidth: theme.pxToRem(180),
  },
  tabsList: {
    marginBottom: 0,
  },
  tabPanel: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    overflow: 'hidden',
  },
  tabsContainer: {
    height: '100%',
    minHeight: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  skeleton: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  loadingContainer: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.black[50],
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  },
  mainContentEmpty: {
    backgroundColor: theme.palette.background.paper,
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    paddingBottom: theme.spacing(6),
  },
  emptyPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.boxShadows.table,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: theme.spacing(6),
  },
  auditMasterDetailRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column' as const,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  /**
   * Rule descriptions are long, so clip each option's two lines instead of
   * letting them wrap the dropdown into a wall of text.
   */
  ruleActionFilterOption: {
    '& > div': {
      minWidth: 0,
      width: '100%',
      '& > *': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
  bodyContent: {
    backgroundColor: theme.palette.background.paper,
    flex: 1,
    height: '100%',
    display: 'flex',
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
  },
  auditListCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.25),
    minWidth: 0,
    width: '100%',
  },
  auditListCellSecondary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  auditTableWrapper: {
    '& td': {
      wordBreak: 'normal',
      overflowWrap: 'normal',
    },
  },
  tableWrapper: {
    width: '100%',
    '& table': {
      boxShadow: 'none',
      width: '100%',
    },
    '& td': {
      overflow: 'hidden',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    },
    '& tr[data-selected="true"]': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? `${alpha(theme.palette.primary.main, 0.18)} !important`
          : `${alpha(theme.palette.graphite.main, 0.05)} !important`,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: theme.pxToRem(4),
        height: '100%',
        backgroundColor: theme.palette.primary.main,
        zIndex: 1,
      },
    },
  },
  /**
   * Shared left-hand list column for every master/detail tab (Rules, Document
   * Matchers, Codings, Document Types, Guidance, audit lists).
   */
  panelListPane: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column' as const,
    maxWidth: theme.pxToRem(600),
    minHeight: 0,
    minWidth: 0,
  },
  /**
   * Shared list filter/search bar: a bordered row at the top of the list pane
   * holding optional filter Select(s) on the left and a search field on the
   * right. `alignItems: flex-start` keeps the filter from shifting when the
   * search field renders helper text below it.
   */
  panelListFilters: {
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    padding: theme.spacing(2),
  },
  /**
   * Shared reading-pane header: a bordered toolbar with per-item actions
   * (Edit / Activate / Approve / Delete, or Save / Cancel while editing) on the
   * left and the tab's primary New/Create action pinned to the right.
   */
  panelDetailToolbar: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    justifyContent: 'space-between',
    maxHeight: theme.pxToRem(60),
    minHeight: theme.pxToRem(48),
    padding: theme.spacing(1.5),
    '& button': {
      paddingLeft: theme.pxToRem(14),
      paddingRight: theme.pxToRem(14),
    },
  },
  panelDetailToolbarGroup: {
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  tableScrollContainer: {
    overflowY: 'auto',
    overflowX: 'auto',
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0,
    '& table thead': {
      display: 'none',
    },
  },
  /**
   * Scroll container for full multi-column tables (the Agent/User stats
   * dashboards) that, unlike the master-detail list panes, must keep their
   * column headers. Mirrors `tableScrollContainer` but leaves `thead` visible
   * and pins it to the top while the body scrolls.
   */
  statsTableScrollContainer: {
    overflowY: 'auto',
    overflowX: 'auto',
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0,
    '& table thead': {
      position: 'sticky' as const,
      top: 0,
      zIndex: 1,
      backgroundColor: theme.palette.background.paper,
    },
  },
  /**
   * No top padding: the pane's first child is the detail toolbar, which should
   * line up with the list pane's filter bar across the divider. Insetting it
   * left the two columns starting at different heights.
   */
  readingPane: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.black[50],
    borderLeft: `1px solid ${theme.palette.divider}`,
    flex: '1.5 1 0',
    flexDirection: 'column' as const,
    minHeight: 0,
    minWidth: 0,
    gap: theme.spacing(2),
    overflow: 'hidden',
    padding: theme.spacing(0, 2, 2),
  },
  detailPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.boxShadows.table,
    flex: 1,
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    minHeight: 0,
    overflowY: 'auto',
  },
  readingPaneHeader: {
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    padding: `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(
      0,
    )} ${theme.spacing(3)}`,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  divider: {
    margin: `${theme.spacing(1)} ${theme.spacing(-3)} 0`,
    width: `calc(100% + ${theme.spacing(6)})`,
  },
  detailBody: {
    padding: theme.spacing(3),
  },
  detailMarkdownViewer: {
    padding: 0,
  },
  ruleListNoMatches: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    textAlign: 'center' as const,
  },
  /** Rules list card: summary line + status, friendly trigger + priority */
  ruleListCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
  },
  ruleListCardHeader: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    width: '100%',
  },
  ruleListSummary: {
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
  },
  ruleListMetaRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    minWidth: 0,
  },
  /** WHEN / IF / THEN section blocks in the detail panel */
  ruleSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  ruleSectionHeader: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  ruleSectionIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    height: theme.pxToRem(16),
    width: theme.pxToRem(16),
  },
  ruleSectionLabel: {
    color: theme.palette.text.secondary,
    letterSpacing: theme.pxToRem(0.5),
  },
  /** Two-column metadata grid in the collapsible Details section */
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: `${theme.pxToRem(120)} minmax(0, 1fr)`,
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5),
    alignItems: 'center',
  },
  metadataLabel: {
    color: theme.palette.text.secondary,
  },
  metadataValue: {
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
  },
  metadataIdRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  mutedText: {
    color: theme.palette.text.secondary,
  },
  /** Stacked list of recent rule executions in the detail panel */
  executionList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
  },
  /** Single execution row: action/channel/timestamp on the left, status + subject on the right */
  executionRow: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    minWidth: 0,
    paddingBottom: theme.spacing(1),
    width: '100%',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  executionRowMeta: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column' as const,
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  /** Container for the rendered mermaid flow diagram in the rule details */
  flowDiagram: {
    display: 'flex',
    justifyContent: 'flex-start',
    overflowX: 'auto' as const,
    width: '100%',
    '& svg': {
      display: 'block',
      flex: '0 0 auto',
      height: 'auto',
      maxWidth: 'none',
      overflow: 'visible',
    },
    '& foreignObject': {
      overflow: 'visible',
    },
    '& .nodeLabel, & .edgeLabel': {
      lineHeight: 1.3,
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  },
  /** Row for “Condition” label + clamped preview in the rules list */
  ruleListConditionRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
  },
  /**
   * react-clamp-lines uses clientHeight; without wrapping, long tokens stay one line
   * tall and the clamp never truncates. Force wrap + bound width via flex parent.
   */
  ruleListConditionClamp: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    '& > p': {
      margin: 0,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
  },
}));
